import { CartLineItem, GProduct, ISpecial } from '@/types';

/** Pre-Cart BOGO/BUNDLE deal context for Counter add flows. */
type PreCartSpecialContext = {
  /** Deal id persisted on the cart line. Undefined for ambiguous multi-deal SKUs. */
  bundleId?: string;
  /**
   * Deal used only for notify UX when there is no persistable bundleId
   * (e.g. catalog add of a SKU that belongs to several Pre-Cart deals).
   * Never persisted on the cart line (avoids assigning the wrong deal on multi-deal SKUs).
   */
  notifyBundleId?: string;
  /** True when add is from deal page (explicit bundle_id on product or cart line). */
  isDealPageAdd: boolean;
};

const PRE_CART_AFFINITY = 'pre-cart';
const PRE_CART_SPECIAL_METHODS = new Set(['BOGO', 'BUNDLE']);

/** Reads the env threshold used to treat a sale price as a GET (penny) line. */
const getPennyThreshold = () =>
  parseInt(process.env.NEXT_PUBLIC_DISCOUNT_MINIMUM_THRESHOLD_VALUE || '0', 10);

/** True when sale price is at or below the env penny threshold (BOGO GET line). */
const isPennySpecialPrice = (salePrice = Infinity) =>
  salePrice <= getPennyThreshold();

const isInSpecialProductList = (
  products: ISpecial['depProducts'],
  productId?: string,
) => !!productId && (products ?? []).some(product => product?.id === productId);

const isPreCartAffinity = (affinity?: string) =>
  affinity?.toLowerCase() === PRE_CART_AFFINITY;

const hasPreCartDiscountForSpecial = (
  line: CartLineItem | GProduct,
  specialId: string,
): boolean =>
  !!line.discounts?.some(
    discount =>
      discount.discount_id === specialId &&
      isPreCartAffinity(discount.discount_affinity),
  );

const getPreCartSpecialDiscounts = (product: GProduct | CartLineItem) =>
  product.discounts?.filter(
    discount =>
      isPreCartAffinity(discount.discount_affinity) &&
      PRE_CART_SPECIAL_METHODS.has(
        discount.discount_method?.toUpperCase() ?? '',
      ),
  ) ?? [];

const collectGroupProductIds = (
  groupData: Record<string, string[]>,
): Set<string> => new Set(Object.values(groupData).flat());

/**
 * Whether a cart line counts toward a deal page tracker (route-scoped).
 * Requires Pre-Cart discount for the special; group membership when the deal has BUY groups.
 */
const isLineEligibleForSpecial = (
  line: CartLineItem,
  specialId: string,
  groupProductIds: Set<string>,
  allowDiscountProducts: boolean,
): boolean => {
  if (line.bundle_id === specialId) {
    return true;
  }

  if (!hasPreCartDiscountForSpecial(line, specialId)) {
    return false;
  }

  if (allowDiscountProducts) {
    return true;
  }

  return !!line.id && groupProductIds.has(line.id);
};

/** Cart lines that contribute to progress for the special on the current deal page route. */
const getCartLinesForSpecial = (
  cartProducts: CartLineItem[],
  specialId: string | undefined,
  groupProductIds: Set<string>,
  allowDiscountProducts: boolean,
): CartLineItem[] => {
  if (!specialId) {
    return [];
  }

  return cartProducts.filter(line =>
    isLineEligibleForSpecial(
      line,
      specialId,
      groupProductIds,
      allowDiscountProducts,
    ),
  );
};

/**
 * Whether this SKU is the GET side of a BOGO deal.
 * Group lists take priority; penny threshold is used when the SKU is ambiguous.
 */
const isBogoGetProduct = (
  special: ISpecial,
  productId: string,
  salePrice = Infinity,
): boolean => {
  const inDep = isInSpecialProductList(special.depProducts, productId);
  const inInd = isInSpecialProductList(special.indProducts, productId);

  if (inDep && !inInd) {
    return true;
  }

  if (inInd && !inDep) {
    return false;
  }

  return isPennySpecialPrice(salePrice);
};

/**
 * Resolves which Pre-Cart special applies on catalog add (BOGO or BUNDLE).
 *
 * - bundleId: persisted on the line. Explicit (deal page / existing line) or inferred
 *   when there is exactly one Pre-Cart deal on the product (or one matching cart deal).
 * - notifyBundleId: UX-only fallback when bundleId is ambiguous (multi-deal SKU).
 */
const resolvePreCartSpecialContext = (
  product: GProduct | CartLineItem,
  cartProducts: CartLineItem[],
  cartLineBundleId?: string,
): PreCartSpecialContext => {
  const explicitBundleId =
    (product as CartLineItem)?.bundle_id ?? cartLineBundleId;

  const preCartDiscounts = getPreCartSpecialDiscounts(product);

  const cartBundleIds = new Set(
    cartProducts
      .map(line => line.bundle_id)
      .filter((lineId): lineId is string => Boolean(lineId)),
  );

  const matchingCartDeals = preCartDiscounts.filter(
    discount => discount.discount_id && cartBundleIds.has(discount.discount_id),
  );

  let inferredBundleId: string | undefined;

  if (preCartDiscounts.length === 1) {
    inferredBundleId = preCartDiscounts[0]?.discount_id;
  } else if (matchingCartDeals.length === 1) {
    inferredBundleId = matchingCartDeals[0]?.discount_id;
  }

  const bundleId = explicitBundleId ?? inferredBundleId;

  // Used for toast/progress only when bundleId is ambiguous (multi-deal SKU).
  // Never persisted on the cart line.
  const notifyBundleId = bundleId ?? preCartDiscounts[0]?.discount_id;

  return {
    bundleId,
    notifyBundleId,
    isDealPageAdd: !!explicitBundleId,
  };
};

/** Per-deal completion state used by cart badge resolution. */
export type CartBadgeDealState = {
  dealId: string;
  bundleComplete: boolean;
};

/** Resolved cart badge context — read-only, never persisted on the line. */
export type CartBadgeDealContext = {
  showBadge: boolean;
  badgeDealId?: string;
};

/** Deal ids to evaluate for a cart line badge (explicit bundle_id or all Pre-Cart deals). */
const resolveCartBadgeDealIds = (product: CartLineItem): string[] => {
  if (product.bundle_id) {
    return [product.bundle_id];
  }

  return getPreCartSpecialDiscounts(product)
    .map(discount => discount.discount_id)
    .filter((dealId): dealId is string => Boolean(dealId));
};

/**
 * Cart badge: show only while every applicable deal on the line is still incomplete.
 * Multi-deal SKU: hide if any applicable deal is already complete.
 */
const resolveCartBadgeDealContext = (
  dealStates: CartBadgeDealState[],
): CartBadgeDealContext => {
  if (!dealStates.length) {
    return { showBadge: false };
  }

  const showBadge = dealStates.every(deal => !deal.bundleComplete);
  const badgeDealId = dealStates.find(deal => !deal.bundleComplete)?.dealId;

  return { showBadge, badgeDealId };
};

/** GET-side detection for notify — group lists on deal page; price-only on catalog. */
const isBogoGetAdd = (
  special: ISpecial,
  productId: string,
  salePrice: number,
  isDealPageAdd: boolean,
): boolean => {
  if (isDealPageAdd) {
    return isBogoGetProduct(special, productId, salePrice);
  }

  return isPennySpecialPrice(salePrice);
};

export {
  collectGroupProductIds,
  getCartLinesForSpecial,
  getPreCartSpecialDiscounts,
  isBogoGetAdd,
  isBogoGetProduct,
  resolveCartBadgeDealContext,
  resolveCartBadgeDealIds,
  resolvePreCartSpecialContext,
};
